import styled from '@emotion/styled';

/**
 * ScenarioCardsPage — Page 8 of the Kuutio print report.
 *
 * 2x2 grid of scenario card images with quotes below.
 * All positions from IDML Spread_ud6.xml via idml-to-css.mjs.
 *
 * Images (2x2 grid, each 349.4 x 196.6pt):
 *   u247: left:40pt top:78.2pt — 1_päivän_esitys-3.jpg (Demokratia)
 *   u24e: left:425.8pt top:78.2pt — 1_päivän_esitys.jpg (Väestö)
 *   u251: left:40pt top:316pt — 1_päivän_esitys-4.jpg (Teknologia)
 *   u254: left:428pt top:316pt — ilmasto.png (Ilmasto)
 * Red dots (Puolukka ovals) mark selected scenarios
 */
export default function ScenarioCardsPage({
  instanceNumber = '3',
  topic = 'Kansainvälisten opiskelijoiden työllistyminen Suomeen',
}) {
  return (
    <Page className="page">
      {/* Title: Pikkuotsikko style */}
      <SubTitle>Kuution vakioskenaarioiden tulevaisuuskuvien tiivistelmät</SubTitle>
      <YellowRule />

      <Intro>
        Osallistujien valitsemat tulevaisuuskuvat on merkattu punaisella pallolla, ja niiden alla on tiivistys valintaperusteesta.
      </Intro>

      {/* 2x2 scenario card grid */}
      <CardImage style={{ left: '40.0pt', top: '78.2pt' }}>
        <img src="/assets/report/1_päivän_esitys-3.jpg" alt="Demokratia 2050" />
      </CardImage>
      <CardImage style={{ left: '425.8pt', top: '78.2pt' }}>
        <img src="/assets/report/1_päivän_esitys.jpg" alt="Väestö 2070" />
      </CardImage>
      <CardImage style={{ left: '40.0pt', top: '316.0pt' }}>
        <img src="/assets/report/1_päivän_esitys-4.jpg" alt="Teknologia 2050" />
      </CardImage>
      <CardImage style={{ left: '428.0pt', top: '316.0pt' }}>
        <img src="/assets/report/ilmasto.png" alt="Ilmasto 2100" />
      </CardImage>

      {/* Red selection dots */}
      <RedDot style={{ left: '316.8pt', top: '238.7pt' }} />
      <RedDot style={{ left: '705.6pt', top: '238.7pt' }} />
      <RedDot style={{ left: '111.2pt', top: '478.8pt' }} />
      <RedDot style={{ left: '495.3pt', top: '478.8pt' }} />

      {/* Quotes below each card */}
      <Quote style={{ left: '40.0pt', top: '280.0pt', width: '345.8pt' }}>
        "Meillä oli se ajatus tässä se, että ylipäätänsä halutaan, että kun me täältä
        lähdetään pois, niin meillä on mielessä positiivisia uusia ideoita, ja siksi
        valitsimme rakennemuutosten aikakausi -tulevaisuuskuvan."
      </Quote>
      <Quote style={{ left: '425.8pt', top: '278.7pt', width: '351.7pt' }}>
        "Valittiin tuo numero kolme, korkean kasvun polku ja tämmöisen hiljaisen
        viestinnän kautta viestitään sitä, että ehdottomasti halutaan, että meillä on
        paljon kansainvälisiä tutkinto-opiskelijoita, jotka tulee Suomeen opiskelemaan".
      </Quote>
      <Quote style={{ left: '40.0pt', top: '521.5pt', width: '381.0pt' }}>
        "Valistimme Tasapaino etsimässä -tulevaisuuskuvan, ja tässä ehkä se, että oma
        harkinta on vielä sellainen, jota tulevaisuudessakin tarvitaan, ja sitten kuitenkin
        joudutaan käymään dialogia, kun etiikka-, sääntely- ja valtarakenteita haastetaan."
      </Quote>
      <Quote style={{ left: '428.7pt', top: '522.3pt', width: '353.1pt' }}>
        "Me lähestyttiin tätä niin, että on myös toiveikas mahdollisuus ajatella niin,
        että tässä Keskitie-vaihtoehdossa voidaan vielä tehdä jotain, ja siellä on vielä
        mahdollisuuksia uusiin avauksiin, uusiin innovointeihin ja uusiin investointeihin."
      </Quote>

      <PageFooter>
        <FooterLeft><strong>Kuutio #{instanceNumber}:</strong> {topic}</FooterLeft>
        <FooterRight>8</FooterRight>
      </PageFooter>
    </Page>
  );
}

const Page = styled.div`
  width: 297mm;
  min-height: 210mm;
  background: #fff;
  color: #000;
  position: relative;
  font-family: 'Rational', sans-serif;
  box-sizing: border-box;
  overflow: hidden;
  @media print { break-after: page; }
`;

const SubTitle = styled.h2`
  position: absolute;
  left: 41.8pt;
  top: 33.9pt;
  width: 516.3pt;
  font-size: 18pt;
  font-weight: 400;
  font-style: italic;
  margin: 0;
`;

const YellowRule = styled.div`
  position: absolute;
  left: 42.5pt;
  top: 56pt;
  width: calc(297mm - 42.5pt - 28.3pt);
  height: 3pt;
  background: #FBE300;
`;

const Intro = styled.p`
  position: absolute;
  left: 41.8pt;
  top: 59.6pt;
  width: 649.0pt;
  font-size: 11pt;
  line-height: 13pt;
`;

const CardImage = styled.div`
  position: absolute;
  width: 349.4pt;
  height: 196.6pt;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const RedDot = styled.div`
  position: absolute;
  width: 29.6pt;
  height: 29.6pt;
  border-radius: 50%;
  background: #E4002B;
`;

const Quote = styled.p`
  position: absolute;
  font-size: 10pt;
  font-weight: 400;
  font-style: italic;
  line-height: 12pt;
`;

const PageFooter = styled.div`
  position: absolute;
  bottom: 14pt;
  left: 42.5pt;
  right: 28.3pt;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 0.5pt solid rgba(0, 0, 0, 0.3);
  padding-top: 6pt;
`;
const FooterLeft = styled.span` font-size: 8pt; font-style: italic; `;
const FooterRight = styled.span` font-size: 10pt; `;
